import type { WebConversationDecision, WebMeetingApproval } from '../pages/api/conversation-decisions';

/**
 * A guest asked something private in a live meeting, and the owner has to approve it before the agent
 * answers the room. The harness raises a meeting-scoped Decision (no goal, no task) and writes ONE
 * assistant row into the owner-only approval thread, carrying `metadata.meetingApproval`. The chat shows
 * an Approve / Decline card under that row, answered on the same verdict route every decision uses.
 *
 * The row names the decision; the decision's live state comes from `/api/conversation-decisions`. The
 * marker alone is never enough to render buttons — a closed or unknown decision must not look open.
 *
 * Mirrors iOS's and Android's `MeetingApproval`.
 */
export interface MeetingApprovalMarker {
  decisionId: string;
}

/** Read `metadata.meetingApproval` off a row. Fail-closed: no decision id, no marker. */
export function readMeetingApprovalMarker(metadata: unknown): MeetingApprovalMarker | undefined {
  const raw = (metadata as { meetingApproval?: unknown } | null | undefined)?.meetingApproval;
  if (!raw || typeof raw !== 'object') return undefined;
  const decisionId = (raw as { decisionId?: unknown }).decisionId;
  return typeof decisionId === 'string' && decisionId.trim() ? { decisionId: decisionId.trim() } : undefined;
}

export type MeetingApprovalRow = WebConversationDecision & { meetingApproval: WebMeetingApproval };

export interface MeetingApprovalPlacement {
  /** Decision id → its row, for every assistant row that names a loaded approval. */
  anchored: Map<string, MeetingApprovalRow>;
  /** Open approvals no row on screen names — shown at the foot of the thread so they stay answerable. */
  trailing: MeetingApprovalRow[];
}

/**
 * Where each approval card goes. A card sits under the assistant row that asked it; an OPEN approval
 * whose row is not on screen (older page, or a row written without the marker) goes to the foot of the
 * thread. A CLOSED one with no row is dropped: there is nothing left to do with it, and a settled line
 * floating under unrelated messages explains nothing.
 */
export function placeMeetingApprovals(
  messages: ReadonlyArray<{ role?: string; meetingApproval?: MeetingApprovalMarker }>,
  rows: ReadonlyArray<WebConversationDecision>,
): MeetingApprovalPlacement {
  const approvals = rows.filter((row): row is MeetingApprovalRow => !!row.meetingApproval);
  const named = new Set(
    messages
      .filter((message) => message.role === 'ASSISTANT' && message.meetingApproval)
      .map((message) => message.meetingApproval!.decisionId),
  );
  const anchored = new Map<string, MeetingApprovalRow>();
  const trailing: MeetingApprovalRow[] = [];
  for (const row of approvals) {
    if (named.has(row.decision.id)) anchored.set(row.decision.id, row);
    else if (isOpenDecision(row)) trailing.push(row);
  }
  return { anchored, trailing };
}

export function isOpenDecision(row: WebConversationDecision): boolean {
  return row.decision.status?.trim().toUpperCase() === 'OPEN';
}

/** Whether this thread holds an approval row — the only chat that asks for closed approvals too. */
export function threadHasMeetingApproval(messages: ReadonlyArray<{ role?: string; meetingApproval?: MeetingApprovalMarker }>): boolean {
  return messages.some((message) => message.role === 'ASSISTANT' && !!message.meetingApproval);
}

/**
 * What the chat keys its decisions re-read on, so a verdict cast elsewhere (WhatsApp, another device)
 * reaches the card. An approval thread also counts the SPOKEN rows (`spokenAloud`): the answer heard after an
 * approval is one, and it sits inside the room's span, which `withoutMeetingRoomExchange` hides, so the shown
 * count never moves when it lands. The room's other rows cannot follow a verdict, so they do not restart the
 * read. A proposals thread keeps the shown count; any other chat never re-reads. Mirrors iOS and Android.
 */
export function conversationDecisionsRefreshKey(flags: {
  approvalThread: boolean;
  proposalsThread: boolean;
  shownCount: number;
  spokenCount: number;
}): number | undefined {
  if (flags.approvalThread) return flags.shownCount + flags.spokenCount;
  return flags.proposalsThread ? flags.shownCount : undefined;
}

export const MEETING_APPROVAL_POLL_MS = 15_000;
const MEETING_APPROVAL_POLL_GRACE_MS = 60_000;
/** With no due time on the row, an approval is re-read for this long after it was raised (they expire in 5). */
const MEETING_APPROVAL_POLL_UNDATED_MS = 10 * 60_000;

/**
 * How long until the next re-read of the `open` approvals, or undefined to stop. A decline, expiry or withdraw
 * cast elsewhere writes no row into the thread and fires no duplexer event this chat hears, so nothing else
 * would reach the card. The next read is the poll interval away, or one second after the soonest due time when
 * that comes first. An approval past its due time by more than the grace (or, with no due time, raised more
 * than ten minutes ago) is no longer polled — its expiry failed, and a timer must not run for it forever.
 * Mirrors Android `meetingApprovalPollDelayMs` and iOS `MeetingApproval.pollDelayMs`.
 */
export function meetingApprovalPollDelayMs(open: readonly WebConversationDecision[], nowMs: number): number | undefined {
  let next: number | undefined;
  for (const row of open) {
    const due = row.decision.dueBy ? Date.parse(row.decision.dueBy) : NaN;
    const raised = row.decision.raisedDate ? Date.parse(row.decision.raisedDate) : NaN;
    if (!Number.isNaN(due) && nowMs >= due + MEETING_APPROVAL_POLL_GRACE_MS) continue;
    if (Number.isNaN(due) && !Number.isNaN(raised) && nowMs >= raised + MEETING_APPROVAL_POLL_UNDATED_MS) continue;
    const wait = !Number.isNaN(due) && due + 1_000 > nowMs ? Math.min(MEETING_APPROVAL_POLL_MS, due + 1_000 - nowMs) : MEETING_APPROVAL_POLL_MS;
    next = next === undefined ? wait : Math.min(next, wait);
  }
  return next;
}

/**
 * The line above the question. The asker is a speaker label from mixed audio, so it is always "someone
 * shown as", never a name stated as fact.
 */
export function meetingApprovalAskerLine(approval: WebMeetingApproval): { key: string; params?: Record<string, string> } {
  const asker = approval.askerLabel?.trim();
  const meeting = approval.meetingName?.trim();
  if (asker && meeting) return { key: 'chat.meetingApproval.askedByIn', params: { asker, meeting } };
  if (asker) return { key: 'chat.meetingApproval.askedBy', params: { asker } };
  if (meeting) return { key: 'chat.meetingApproval.askedIn', params: { meeting } };
  return { key: 'chat.meetingApproval.askedInTheMeeting' };
}
